/**
 * chain-eos/config.ts
 *
 * All EOS chain constants in one place.
 * Edit here to update endpoints, contracts, or token configs.
 */

export const EOS_CHAIN_ID =
  'aca376f206b8fc25a6ed44dbdc66547c36c6c33e3a119ffbeaef943642f0e906'

// ─── RPC Endpoints ───────────────────────────────────────────────────────────
// Multiple endpoints for automatic failover via WharfKit

// Endpoints are tried in order, first that answers wins (see accountExists,
// governance.ts, multisig.ts, resources.ts, balance.ts). Dropped 2026-08-27:
// eos.eosrio.io / api.eosn.io (DNS no longer resolves), eos.dfuse.eosnation.io
// (get_account/get_info fail on this host) — all three were dead weight that
// only added console noise and latency before falling through to a working
// endpoint. See docs/LEARNINGS.md.
export const EOS_RPC_ENDPOINTS = [
  'https://eos.eosusa.io',
  'https://api.main.alohaeos.com',
  'https://eos.greymass.com',
]

// Hyperion for history/transfers.
// Multiple endpoints — get_tokens (v2/state) isn't reliably available on every
// history node, so we try them in order and fall back on failure/timeout.
// eos.eosrio.io dropped 2026-08-27 (DNS no longer resolves) — same dead host as
// EOS_RPC_ENDPOINTS above, was still lingering here causing net::ERR_NAME_NOT_RESOLVED.
export const HYPERION_ENDPOINTS = [
  'https://eos.hyperion.eosrio.io',
  'https://eos.eosusa.io',
]
export const HYPERION_ENDPOINT = HYPERION_ENDPOINTS[0]

// ─── Block explorers (account / tx URL templates) ─────────────────────────────

/** PayCash-issued tokens use the PayCash CDN for icons. */
export const PAYCASH_TOKEN_CONTRACTS = ['token.pcash', 'swap.pcash', 'aabw2r.pcash'] as const

/**
 * Verified token contracts — the only ones shown in the wallet by default.
 *
 * Anyone can issue a token called "USDT" on Antelope, so the contract is the only
 * thing that means anything. Everything not listed here is treated as unverified:
 * it is collapsed into the hidden section until the user explicitly pulls it out.
 *
 * Add a contract here only after checking it is what it claims to be.
 */
export const KNOWN_TOKEN_CONTRACTS = [
  'core.vaulta',    // Vaulta (A) — native
  'eosio.token',    // EOS — native
  'tethertether',   // USDT issued natively on EOS
  'usdt.xsat',      // USDT bridged via exSat
  'btc.xsat',       // BTC bridged via exSat (symbol BTC, precision 8)
  ...PAYCASH_TOKEN_CONTRACTS,
] as const

export function isKnownTokenContract(contract: string): boolean {
  return (KNOWN_TOKEN_CONTRACTS as readonly string[]).includes(String(contract ?? '').trim())
}

/**
 * Local icon paths bundled with the app (apps/mobile/public/tokens/).
 * Avoids depending on the (currently broken) paycash.app CDN.
 */
const LOCAL_TOKEN_ICONS: Record<string, string> = {
  A: '/tokens/A.svg',
  EOS: '/tokens/EOS.svg',
  USDT: '/tokens/USDT.png',
  MLNK: '/tokens/token-mlnk.svg',
  RUBCASH: '/tokens/token-rubcash.svg',
  USDCASH: '/tokens/token-usdcash.svg',
  EURCASH: '/tokens/token-eurcash.svg',
  UAHCASH: '/tokens/token-uahcash.svg',
  RMBC: '/tokens/token-rmbc.svg',
  RMBCASH: '/tokens/token-rmbcash.svg',
  USDBX: '/tokens/token-usdbx.svg',
  RUBLES: '/tokens/token-rubles.svg',
  URALS: '/tokens/token-urals.png',
  // Regional "LIST" (local currency) tokens — paycashswap.com uses a shared
  // generic icon for all of these too (token-list.svg), not per-region art.
  LIRUMOW: '/tokens/token-list.svg',
  LIRUCEK: '/tokens/token-list.svg',
  LIRULED: '/tokens/token-list.svg',
  LIUAIEV: '/tokens/token-list.svg',
}

/**
 * Public icon URL for a token (mobile Wallet + extras from Hyperion).
 * Uses locally bundled icons for known tokens; falls back to eosauthority for others.
 */
export function resolveTokenLogoUrl(symbol: string, contract: string): string {
  const sym = String(symbol ?? '').trim()
  const c = String(contract ?? '').trim()
  if (!sym) {
    return ''
  }
  const upper = sym.toUpperCase()
  if (LOCAL_TOKEN_ICONS[upper]) {
    return LOCAL_TOKEN_ICONS[upper]
  }
  // Any other regional "LIST" token (LIRU*, LIUA*, etc.) — same generic icon as paycashswap.com.
  if (upper.startsWith('LI') && upper.length >= 5) {
    return '/tokens/token-list.svg'
  }
  // Note: paycash.app CDN is confirmed broken (returns HTML, not images) — don't use it.
  void c
  return `https://eosauthority.com/assets/tokens/${encodeURIComponent(sym)}.png`
}

export const DEFAULT_EXPLORER_ID = 'eos-navigator'

export const BLOCK_EXPLORERS = [
  {
    id: 'eos-navigator',
    name: 'EOS Navigator',
    url: 'https://eos-explorer.vercel.app/account/{account}',
    txUrl: 'https://eos-explorer.vercel.app/tx/{tx}',
  },
  {
    id: 'blocksfinity',
    name: 'Blocksfinity',
    // TODO: unverified — /account/{account} 404s on the live site (checked
    // 2026-08-27). Falls back to the generic /explore page (has an in-page
    // search) until Dario/Blocksfinity confirms the real deep-link pattern.
    url: 'https://www.blocksfinity.com/explore',
    txUrl: 'https://www.blocksfinity.com/explore',
  },
  {
    id: 'vaultaitems',
    name: 'Vaulta Items',
    url: 'https://vaulta.chainitems.com/account/{account}',
    txUrl: 'https://vaulta.chainitems.com/tx/{tx}',
  },
  {
    id: 'vaultascan',
    name: 'VaultaScan',
    url: 'https://vaultascan.com/account/{account}',
    txUrl: 'https://vaultascan.com/tx/{tx}',
  },
  {
    id: 'eosauthority',
    name: 'EOS Authority',
    url: 'https://eosauthority.com/account/{account}',
    txUrl: 'https://eosauthority.com/tx/{tx}',
  },
  {
    id: 'eosai',
    name: 'EOS AI',
    url: 'https://eosai.com/account/{account}',
    txUrl: 'https://eosai.com/tx/{tx}',
  },
  {
    id: 'unicove',
    name: 'Unicove',
    url: 'https://unicove.com/account/{account}',
    txUrl: 'https://unicove.com/tx/{tx}',
  },
] as const

export function getExplorerUrl(
  explorerId: string,
  type: 'account' | 'tx',
  value: string,
): string {
  const ex = BLOCK_EXPLORERS.find(e => e.id === explorerId) ?? BLOCK_EXPLORERS[0]
  return type === 'account'
    ? ex.url.replace('{account}', value)
    : ex.txUrl.replace('{tx}', value)
}

// ─── Token Contracts ──────────────────────────────────────────────────────────

export const TOKENS = {
  /** Vaulta (A) — native EOS network token, rebranded from EOS */
  A: {
    contract: 'core.vaulta',
    symbol: 'A',
    precision: 4,
    name: 'Vaulta',
    logoUrl: '/tokens/A.svg',
  },
  /** Legacy EOS token (pre-Vaulta swap) — still circulates */
  EOS: {
    contract: 'eosio.token',
    symbol: 'EOS',
    precision: 4,
    name: 'EOS',
    logoUrl: '/tokens/EOS.svg',
  },
  /** USDT on EOS */
  USDT: {
    contract: 'tethertether',
    symbol: 'USDT',
    precision: 4,
    name: 'Tether USD',
    logoUrl: '/tokens/USDT.png',
  },
} as const

export type TokenSymbol = keyof typeof TOKENS

/** Order in which tokens appear in the Wallet screen */
export const TOKEN_DISPLAY_ORDER: TokenSymbol[] = ['A', 'EOS', 'USDT']

// ─── Greymass Fuel ───────────────────────────────────────────────────────────

/**
 * WebDEX deep link for swapping a specific token.
 *
 * The token is identified as `contract:SYMBOL` because a symbol alone is
 * ambiguous on Antelope — two contracts can both issue "USDT".
 * WebDEX reads `from` / `to` from the query string; if it does not yet, the link
 * still opens the exchange, just without a pre-selected token.
 */
export const WEBDEX_URL = 'https://webdex.space'

export function getWebdexSwapUrl(token: { contract: string; symbol: string }, to?: { contract: string; symbol: string }): string {
  const params = new URLSearchParams({ from: `${token.contract}:${token.symbol}` })
  if (to) params.set('to', `${to.contract}:${to.symbol}`)
  return `${WEBDEX_URL}/?${params.toString()}`
}

export const FUEL_URL = 'https://eos.greymass.com'

// ─── Blocksfinity (linked explorer/toolkit) ────────────────────────────────────
// Non-custodial EOS/Vaulta explorer — linked from Discovery and from Tools quick
// links (Name Bids / Top Holders / Alerts). We do not embed its wallet actions;
// it is opened read-only in the in-app dApp browser.
export const BLOCKSFINITY_URL = 'https://www.blocksfinity.com'

// MSIG.app (CryptoLions) — multi-chain eosio.msig tool. The chain is part of the
// path, so linking to `/eos` lands the user straight on EOS/Vaulta instead of the
// chain picker. Our own MultisigScreen only approves/executes existing proposals;
// MSIG.app is where a proposal gets *created*, so we link out to it rather than
// rebuild that form.
export const MSIG_APP_URL = 'https://msig.app/eos'
export const BLOCKSFINITY_EXPLORE_URL = 'https://www.blocksfinity.com/explore'
export const BLOCKSFINITY_NAMEBIDS_URL = 'https://www.blocksfinity.com/namebids'
export const BLOCKSFINITY_TOPHOLDERS_URL = 'https://www.blocksfinity.com/topholders'
export const BLOCKSFINITY_ALERTS_URL = 'https://www.blocksfinity.com/alerts'


// ─── Governance ───────────────────────────────────────────────────────────────

export const SYSTEM_CONTRACT = 'eosio'

// ─── dApps (Discovery list) ───────────────────────────────────────────────────

export const FEATURED_DAPPS = [
  // ── Featured (Discovery grid — featured: true) ─────────────────────────────
  {
    id: 'webdex',
    name: 'WebDEX',
    description: 'Decentralized exchange for EOS/Vaulta',
    url: 'https://webdex.space',
    category: 'defi',
    logo: '/dapps/webdex.png',
    featured: true,
  },
  {
    id: 'vaulta-explorer',
    name: 'Vaulta Explorer',
    description: 'Block explorer for the Vaulta ecosystem',
    url: 'https://eos-explorer.vercel.app',
    category: 'tools',
    logo: '/dapps/eos-explorer.png',
    featured: true,
  },
  {
    id: 'malinkaharvest',
    name: 'Operation Malinka',
    description: 'Collect berries before the bear wakes up',
    url: 'https://mlnkharvest.vercel.app',
    category: 'games',
    logo: 'https://mlnkharvest.vercel.app/graphic/medvedz.png',
    featured: true,
  },

  // ── Blocksfinity / BlockzHub (priority placement, per Dario) ───────────────
  {
    id: 'blocksfinity',
    name: 'Blocksfinity',
    description: 'EOS/Vaulta explorer — name bids, top holders, multisig',
    url: BLOCKSFINITY_URL,
    category: 'tools',
    logo: 'https://www.blocksfinity.com/favicon.ico',
    featured: false,
  },
  {
    id: 'blockzhub',
    name: 'BlockzHub',
    description: 'News and community hub for the Vaulta/EOS ecosystem',
    url: 'https://blockzhub.io/earn_crypto/',
    category: 'tools',
    logo: 'https://blockzhub.io/favicon.ico',
    featured: false,
  },
  {
    id: 'blockzhub-help',
    name: 'BlockzHub Help Center',
    description: 'Support and docs (formerly EOS Support Help Center)',
    url: 'https://help.blockzhub.io',
    category: 'tools',
    logo: 'https://help.blockzhub.io/favicon.ico',
    featured: false,
  },
  {
    id: 'blokstorage',
    name: 'Blokstorage',
    description: 'On-chain storage: encrypted files, hash verification, messaging',
    url: 'https://blokstorage.io',
    category: 'tools',
    logo: 'https://blokstorage.io/favicon.ico',
    featured: false,
  },

  {
    id: 'msigapp',
    name: 'MSIG.app',
    description: 'Create, review and sign eosio.msig proposals (CryptoLions)',
    url: MSIG_APP_URL,
    category: 'tools',
    logo: 'https://msig.app/static/images/favicon/favicon-96x96.png',
    featured: false,
  },

  // ── DeFi ───────────────────────────────────────────────────────────────────
  {
    id: 'defibox',
    name: 'Defibox',
    description: 'AMM swap, lending & stablecoin on EOS',
    url: 'https://defibox.io',
    category: 'defi',
    logo: 'https://defibox.io/favicon.ico',
    featured: false,
  },
  {
    id: 'pizzadefi',
    name: 'Pizza DeFi',
    description: 'Yield farming & liquidity on EOS',
    url: 'https://pizza.finance',
    category: 'defi',
    logo: 'https://pizza.finance/favicon.ico',
    featured: false,
  },

  // ── NFT ────────────────────────────────────────────────────────────────────
  {
    id: 'atomichub',
    name: 'AtomicHub',
    description: 'NFT marketplace on EOS',
    url: 'https://eos.atomichub.io',
    category: 'nft',
    logo: 'https://atomichub.io/favicon.ico',
    featured: false,
  },
  {
    id: 'neftyblocks',
    name: 'NeftyBlocks',
    description: 'NFT creation, packs & drops',
    url: 'https://neftyblocks.com',
    category: 'nft',
    logo: 'https://neftyblocks.com/favicon.ico',
    featured: false,
  },

  // ── Tools ──────────────────────────────────────────────────────────────────
  {
    id: 'unicove',
    name: 'Unicove',
    description: 'Greymass wallet & account tools',
    url: 'https://unicove.com',
    category: 'tools',
    logo: 'https://unicove.com/favicon.ico',
    featured: false,
  },
  {
    id: 'vaultaitems',
    name: 'Vaulta Items',
    description: 'EOS block explorer, BPs, holders & RAM',
    url: 'https://vaulta.chainitems.com',
    category: 'tools',
    logo: 'https://vaulta.chainitems.com/favicon.ico',
    featured: false,
  },
  {
    id: 'eosauthority',
    name: 'EOS Authority',
    description: 'Block explorer, voter info & key tools',
    url: 'https://eosauthority.com',
    category: 'tools',
    logo: 'https://eosauthority.com/favicon.ico',
    featured: false,
  },
  {
    id: 'bloks',
    name: 'Bloks.io',
    description: 'EOS block explorer & contract reader',
    url: 'https://bloks.io',
    category: 'tools',
    logo: 'https://bloks.io/favicon.ico',
    featured: false,
  },
] as const

export type DApp = (typeof FEATURED_DAPPS)[number] & { featured?: boolean }
